import {
  CANAIS,
  RESULTADOS,
  limitesDoDia,
  type Canal,
  type Pessoa,
  type Resultado,
  type Visita,
} from '@sheafyield/dominio';
import { apagar, gravar, type Banco } from '@sheafyield/sync';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import {
  banco as bancoDoApp,
  cliente as clienteDoApp,
} from '../app/contexto.js';
import {
  pessoasPorNome,
  publicacoesUsadas,
  ultimaPublicacaoUsada,
} from '../dados/consultas.js';
import { donoDaSessao, type DonoDasLinhas } from '../dados/dono.js';
import { haQuantoTempo } from '../dados/tempoRelativo.js';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { CampoLongo } from '../ds/CampoLongo.js';
import { Folha } from '../ds/Folha.js';
import { GrupoDeChips } from '../ds/GrupoDeChips.js';
import { ICONES } from '../ds/icones.js';
import { Toast } from '../ds/Toast.js';
import { ROTULO_DO_RESULTADO } from '../hoje/VisitasDoDia.js';
import { CampoProximaVisita, lerProximaVisita } from './CampoProximaVisita.js';
import { CampoPublicacao } from './CampoPublicacao.js';
import { gravarRascunho, lerRascunho, limparRascunho } from './rascunho.js';
import './registrar.css';

/** O dono do app, amarrado uma vez — o cliente é singleton (mesma amarração
 *  do `Cronometro` e da tela Hoje). */
const donoDoApp = donoDaSessao(clienteDoApp);

/** O desenho de cada resultado (prancha 02.1). O rótulo continua sendo quem
 *  nomeia o chip: o ícone é `aria-hidden` por dentro do `Icone`, e existe
 *  para achar o chip certo de relance, com o polegar já a caminho. */
const ICONE_DO_RESULTADO: Record<Resultado, ReactNode> = {
  nao_estava: ICONES.naoEstava,
  conversamos: ICONES.conversamos,
  nao_interessado: ICONES.semInteresse,
  deixei_publicacao: ICONES.publicacao,
  revisita: ICONES.revisita,
  estudo: ICONES.estudo,
};

/** Os dois resultados que criam ficha ganham tinta; os quatro que encerram a
 *  visita ficam neutros. `undefined` é a ausência de tinta, não um valor. */
const TINTA_DO_RESULTADO: Partial<Record<Resultado, string>> = {
  revisita: 'ambar',
  estudo: 'verde',
};

/** O canal do caminho curto (spec §8: "canal = porta"). Trocar de canal é
 *  assunto do caminho longo; na porta, que é onde 95% das visitas
 *  acontecem, perguntar custaria o toque que esta tela existe para poupar. */
const CANAL_PADRAO: Canal = 'porta';

/** Os cinco canais da spec §4 em português de gente. */
export const ROTULO_DO_CANAL: Record<Canal, string> = {
  porta: 'Porta',
  telefone: 'Telefone',
  carta: 'Carta',
  carrinho: 'Carrinho',
  informal: 'Informal',
};

const OPCOES_DE_CANAL = CANAIS.map((canal) => ({
  valor: canal,
  rotulo: ROTULO_DO_CANAL[canal],
}));

/** Quantos segundos o aviso de "Visita registrada" espera antes de sumir —
 *  a janela de Desfazer do brief. */
const SEGUNDOS_DE_DESFAZER = 6;

/** A ficha que a gravação criou ou alterou, e como ela estava antes.
 *
 *  É o que faz o Desfazer desfazer **a visita inteira**, e não só a linha da
 *  visita: registrar uma "Revisita" com nome cria ou muda uma `pessoa`, e um
 *  desfazer que só sepultasse a `visita` deixaria para sempre um interessado
 *  que ninguém quis cadastrar na tira "Para visitar" de Hoje — ou, pior, uma
 *  ficha que já existia com o `status`, a `proxima_visita_em` e a grafia do
 *  nome sobrescritos e sem volta. */
interface FichaMexida {
  id: string;
  /** Como a ficha estava **antes** desta gravação; `null` quando ela não
   *  existia e foi criada agora.
   *
   *  É um retrato de até seis segundos atrás — o da hora da gravação —, e
   *  isso é escolha: se um ciclo de sync aplicar uma versão remota da mesma
   *  pessoa nesse intervalo, o Desfazer a sobrescreve com o retrato local. O
   *  Desfazer é **do toque**, não do estado do mundo: quem apertou "Desfazer"
   *  quer o que havia antes de tocar no chip, e a alternativa (reler a ficha
   *  na hora de desfazer) devolveria uma linha que a pessoa nunca viu. A
   *  janela é curta, o LWW resolve o resto, e a versão remota volta no ciclo
   *  seguinte se for a mais nova. */
  anterior: Pessoa | null;
}

/** Tudo o que o toque no resultado decidiu. A pergunta posterior pede só a
 * identidade: editar campos enquanto ela está aberta não pode reescrever a
 * visita que já estava pronta para ser gravada. */
interface IntencaoDeRegistro {
  resultado: Resultado;
  nome: string;
  /** Distingue o nome que a pessoa escreveu do vazio transitório enquanto
   *  uma ficha ligada ainda está carregando. */
  nomeEditado: boolean;
  onde: string;
  publicacao: string | null;
  canal: Canal;
  proxima: ReturnType<typeof lerProximaVisita>;
}

interface DuvidaDePessoa {
  intencao: IntencaoDeRegistro;
  candidatas: Pessoa[];
  donoAtual: string;
  ultimaPublicacao: string | undefined;
}

export interface DepsDeRegistrar {
  banco: Banco;
  /** De quem é a linha que está sendo escrita. Injetado, como no
   *  `Cronometro`: o dono vem da sessão de autenticação, e o componente não
   *  tem por que saber de onde. */
  dono: DonoDasLinhas;
  aberta: boolean;
  /** A ficha a que esta visita já nasce ligada — o que a tira "Para visitar
   *  hoje" passa ao ser tocada. Sem ele a folha é a de sempre: a ficha, se
   *  houver, sai do nome digitado no caminho longo. */
  pessoaId?: string;
  aoFechar: () => void;
}

function comoErro(causa: unknown): Error {
  return causa instanceof Error ? causa : new Error(String(causa));
}

function erroDeFichaObsoleta(): Error {
  return new Error(
    'A ficha escolhida não está mais disponível. Escolha novamente.',
  );
}

/** Se um `proxima_visita_em` é o compromisso **de hoje ou de antes** — o
 *  mesmo recorte de `paraVisitarHoje`, que é quem põe a pessoa na tira. Data
 *  ilegível conta como vencida: o que não se consegue ler não serve para
 *  chamar ninguém de volta.
 *
 *  É este recorte, e não `< agora`, porque é ele que dá vazão à tira:
 *  registrar uma visita a quem estava marcado para hoje **encerra** o
 *  compromisso de hoje, mesmo que a hora combinada ainda não tivesse chegado.
 *  O que vem depois é uma decisão nova, e ela se toma no campo "Próxima
 *  visita". */
function ehCompromissoDeHoje(quando: string | null | undefined): boolean {
  if (quando === null || quando === undefined) return true;
  const em = Date.parse(quando);
  return Number.isNaN(em) || em <= Date.parse(limitesDoDia(new Date()).ate);
}

/**
 * A folha Registrar: **dois toques e menos de cinco segundos** (spec §8).
 *
 * Toque 1 abre a folha — o botão largo de Hoje ou o atalho `/registrar` da
 * Tela de Início. Toque 2 é o chip do resultado, e **o chip é o botão de
 * salvar**: grava a visita, fecha a folha e mostra o aviso com Desfazer.
 * Não há "Salvar" no caminho curto; um passo a mais aqui é um registro que
 * não acontece na porta seguinte.
 *
 * O resto vem preenchido, sem perguntar (spec §8): hora igual a agora,
 * canal `porta`, publicação igual à última usada. O endereço "em foco"
 * seria a última casa tocada; enquanto não há território não há casa
 * nenhuma, então o campo nasce vazio e pronto para digitar.
 *
 * **O caminho longo mora atrás de "Detalhes"** — nome, publicação com
 * autocompletar, canal e próxima visita em linguagem natural. Ele é o
 * equivalente acessível do arrasto que a spec §8 descreve: a `Folha` não tem
 * gesto de arrasto nenhum, e um gesto seria de qualquer forma o pior jeito de
 * revelar conteúdo para quem usa VoiceOver ou Controle por Voz. É um botão
 * com `aria-expanded`, e a região que ele revela fica **abaixo** dos chips,
 * porque em AX5 o que estiver acima deles os empurra para fora da vista.
 *
 * **Por que os chips são botões e não o `GrupoDeChips`.** O chip do design
 * system é um rádio, e num grupo de rádios a seta do teclado (e o gesto do
 * VoiceOver) **move a seleção**, disparando `change` em cada parada. Como
 * aqui escolher é gravar, navegar pelo grupo gravaria uma visita por chip
 * percorrido. O que estes seis controles fazem é executar uma ação, e o
 * elemento de executar ação é o botão. (O canal, no caminho longo, é
 * `GrupoDeChips` de verdade: lá escolher é só escolher.)
 *
 * **Por que os chips vêm antes do campo "Onde?".** O critério duro é o
 * segundo toque: em AX5 o corpo da folha rola, e o que estiver embaixo nasce
 * fora da vista. Com os chips no topo do corpo eles estão à mão assim que a
 * folha sobe, em qualquer tamanho de texto — e, como a folha se ancora na
 * borda de baixo, em tamanho normal eles caem inteiros na metade inferior da
 * tela, que é a outra metade do critério ("uma mão"). O "endereço em foco" da
 * spec é sobre **qual** endereço vem escolhido, não sobre a ordem na folha: é
 * um item da lista de padrões.
 *
 * **Em AX5 os seis não cabem, e não é o cabeçalho.** Medido no WebKit a
 * 375×667 com a raiz em 53 px, já com o cromo da `Folha` no osso — título
 * fora da vista, puxador fora do layout e "Fechar" com teto de 48 px:
 * **quatro** chips inteiros à vista numa tela sem área segura nenhuma, e
 * **três** no aparelho de verdade, com os dois recuos do iPhone com entalhe
 * (59 px em cima, 34 px embaixo), onde o corpo da folha vai de 116 a 633 e
 * "Publicação" fica cortada pela dobra — que é a pista de que há mais para
 * rolar. O quarto contava como inteiro enquanto só o recuo de cima era
 * injetado; o número caiu em 07/09/2026 porque a medida parou de mentir, não
 * porque o desenho piorou. Os seis somam 738 px de pilha contra ~517 px de
 * corpo — a conta não fecha por rótulo nenhum. O que se garante é o segundo
 * toque à mão; `e2e/registrar.spec.ts` afirma os números, medindo com e sem
 * a área segura, e traz também o quanto sobra na zona do polegar.
 *
 * Esta é a forma injetável, para teste. A rota usa `FolhaRegistrar`.
 */
export function FolhaRegistrarCom({
  banco,
  dono,
  aberta,
  pessoaId,
  aoFechar,
}: DepsDeRegistrar) {
  // O que estava digitado quando a árvore foi desmontada. Lido **uma vez**,
  // no primeiro render, e só quando a folha já **nasce** aberta. Quem nasce
  // aberta é a remontagem da trava (o endereço continua sendo
  // `/hoje?registrar=1`), que é o caso para o qual isto existe — mas também
  // qualquer entrada direta nesse endereço, e o componente não tem como
  // distinguir as duas. Adotar o rascunho também nessa é aceitável porque a
  // `sessionStorage` morre com a aba: o que ele encontra é rascunho da mesma
  // aba e da mesma sessão de uso, nunca de outro dia.
  //
  // O que **não** adota rascunho é abrir a folha a partir da tela já montada:
  // essa é a transição `false → true`, e é ela que passa pela limpeza mais
  // abaixo, que esvazia os campos e apaga o rascunho — a folha nova é uma
  // visita nova. Ver `rascunho.ts`.
  const [rascunho] = useState(() => (aberta ? lerRascunho() : null));
  const [onde, setOnde] = useState(rascunho?.onde ?? '');
  /** Qual chip está gravando, se algum. Um só de cada vez: dois toques
   *  rápidos em dois chips não podem virar duas visitas. */
  const [salvando, setSalvando] = useState<Resultado | null>(null);
  const [erroDeGravacao, setErroDeGravacao] = useState<Error | null>(null);
  /** A gravação que parou para perguntar qual ficha. `null` quando não há
   *  dúvida — o caso comum continua sendo o caminho de dois toques. */
  const [duvida, setDuvida] = useState<DuvidaDePessoa | null>(null);
  const [fichaEmGravacao, setFichaEmGravacao] = useState<string | null>(null);
  /** A visita da vez, a que o Desfazer apaga. Sobrevive ao fechamento da
   *  folha de propósito: é depois de fechar que o aviso aparece. */
  const [registrada, setRegistrada] = useState<Visita | null>(null);
  /** A ficha que a mesma gravação mexeu, se mexeu em alguma. Anda **junto**
   *  com `registrada`, e pela mesma razão: é o par que o Desfazer desfaz. */
  const [fichaMexida, setFichaMexida] = useState<FichaMexida | null>(null);
  const [falha, setFalha] = useState<string | null>(null);

  // O caminho longo. Tudo nasce vazio e nada dele é obrigatório: a folha
  // grava igual com os detalhes fechados.
  const [detalhes, setDetalhes] = useState(rascunho?.detalhes ?? false);
  const [nome, setNome] = useState(rascunho?.nome ?? '');
  /** `null` é "ninguém encostou": vale a última publicação usada, lida na
   *  hora de gravar. Depois que os detalhes abrem, o campo passa a mandar —
   *  inclusive quando a pessoa o apaga de propósito. */
  const [publicacao, setPublicacao] = useState<string | null>(
    rascunho?.publicacao ?? null,
  );
  const [publicacoes, setPublicacoes] = useState<readonly string[]>([]);
  const [canal, setCanal] = useState<Canal>(rascunho?.canal ?? CANAL_PADRAO);
  const [proximaTexto, setProximaTexto] = useState(
    rascunho?.proximaTexto ?? '',
  );
  /** O instante contra o qual "sábado 10h" é resolvido, fixado quando os
   *  detalhes abrem. Fixo, e não `new Date()` a cada tecla, porque o que se
   *  grava tem de ser o que a pré-visualização disse — e uma conta que anda
   *  sozinha não é a mesma conta. `null` enquanto ninguém abriu os detalhes:
   *  aí não há próxima visita nenhuma a interpretar. */
  const [agora, setAgora] = useState<Date | null>(
    rascunho?.agora === undefined || rascunho.agora === null
      ? null
      : new Date(rascunho.agora),
  );
  const idDetalhes = useId();
  const idDicaDaDuvida = useId();
  // A ref fecha a janela entre o primeiro clique e a renderização do estado.
  // A versão também invalida buscas que terminem depois de a folha fechar.
  const gravacaoEmCurso = useRef(false);
  const versaoDaOperacao = useRef(0);
  /** O valor vazio antes do preenchimento da ficha ligada não é uma edição.
   *  Sem esta distinção, um toque rápido num resultado apagaria o nome que
   *  já existe no banco. */
  const nomeFoiEditado = useRef(rascunho?.nomeEditado ?? rascunho !== null);
  const vinculoAnterior = useRef(pessoaId);

  // Abrir a folha limpa a mesa: campo em branco, erro anterior fora e —
  // principalmente — aviso anterior fora. Quem abriu para registrar outra
  // visita já não vai desfazer a de trás, e é essa passagem por nulo que faz
  // o `Toast` anunciar a próxima: a região viva só fala quando o texto muda,
  // e duas visitas seguidas trazem a mesma frase.
  //
  // O ajuste é **durante a renderização**, e não num efeito: é o que o React
  // recomenda para estado derivado de prop (e o que a regra
  // `react-hooks/set-state-in-effect` exige). Termina numa passada.
  const [abertaVista, setAbertaVista] = useState(aberta);
  if (aberta !== abertaVista) {
    setAbertaVista(aberta);
    setDuvida(null);
    setSalvando(null);
    setFichaEmGravacao(null);
    if (aberta) {
      setOnde('');
      setErroDeGravacao(null);
      setRegistrada(null);
      setFichaMexida(null);
      setFalha(null);
      // O caminho longo volta fechado e vazio: a folha nova é uma visita
      // nova, e o nome da casa anterior na tela seria pior do que campo
      // nenhum.
      setDetalhes(false);
      setNome('');
      setPublicacao(null);
      setPublicacoes([]);
      setCanal(CANAL_PADRAO);
      setProximaTexto('');
      setAgora(null);
    }
  }

  // Cobre também quem fecha a folha mudando a prop por fora. Os pedidos da
  // própria folha invalidam imediatamente em `fecharFolha`; repetir aqui é
  // inofensivo e impede uma busca tardia de reaparecer na próxima abertura.
  useEffect(() => {
    if (aberta) return;
    nomeFoiEditado.current = false;
    versaoDaOperacao.current += 1;
    gravacaoEmCurso.current = false;
  }, [aberta]);

  // A ficha ligada, lida uma vez por abertura. O nome dela entra no campo
  // **e** nos detalhes abertos: quem tocou numa linha da tira já disse de
  // quem é a visita, e a folha tem de mostrar isso — senão o segundo toque
  // gravaria uma visita anônima com cara de ligada.
  useEffect(() => {
    if (!aberta || pessoaId === undefined) return;
    // A retomada conserva a intenção de grafia, inclusive antes de a
    // leitura terminar. Só outro vínculo começa com preenchimento automático.
    if (vinculoAnterior.current !== pessoaId) nomeFoiEditado.current = false;
    vinculoAnterior.current = pessoaId;
    let valendo = true;
    void banco.pessoa.get(pessoaId).then(
      (achada) => {
        if (!valendo) return;
        const viva = achada !== undefined && !achada.deleted ? achada : null;
        if (viva === null) return;
        // A leitura pode terminar depois de a pessoa começar a digitar. O
        // preenchimento inicial não ganha de uma edição feita na tela.
        if (!nomeFoiEditado.current) setNome(viva.nome);
        // Os detalhes já entram abertos, com o mesmo preparo do toque em
        // "Detalhes": a base de interpretação da próxima visita fixada agora
        // (sem ela, "em 2 semanas" não vira data nenhuma) e as sugestões de
        // publicação carregadas. O que **não** se repete aqui é a rolagem: a
        // folha acabou de abrir, os chips já estão no topo do corpo, e mexer
        // na rolagem sem um toque que a justifique seria a tela andando
        // sozinha.
        setDetalhes(true);
        setAgora(new Date());
        void publicacoesUsadas(banco).then(
          (lista) => {
            setPublicacoes(lista);
            setPublicacao((atual) => atual ?? lista[0] ?? '');
          },
          () => {
            setPublicacoes([]);
          },
        );
      },
      () => {
        // Falhou só o preenchimento inicial. A gravação relê o `pessoaId`,
        // então uma falha passageira aqui não troca a identidade por uma
        // homônima nem impede nova tentativa.
      },
    );
    return () => {
      valendo = false;
    };
  }, [aberta, pessoaId, banco]);

  // O rascunho é gravado **a cada tecla e sem atraso**, enquanto a folha
  // estiver aberta. Sem isto, qualquer ida ao segundo plano com a trava em
  // "Ao abrir" apagava o que estava digitado: o `PortaoDaTrava` não monta o
  // conteúdo enquanto trancado, e ir ao Mapas conferir o número da casa
  // desmonta esta árvore inteira. Um atraso de algumas centenas de
  // milissegundos economizaria escritas de duzentos bytes e reabriria
  // justamente a janela que isto fecha — o app saindo entre a última tecla e
  // o disparo do relógio. Ver `rascunho.ts`.
  useEffect(() => {
    if (!aberta) {
      // Folha fechada não tem rascunho: nem o da visita que acabou de ser
      // gravada, nem o de quem desistiu e fechou. O nome e o endereço de uma
      // pessoa real não ficam guardados um segundo a mais do que precisam.
      limparRascunho();
      return;
    }
    gravarRascunho({
      onde,
      nome,
      nomeEditado: nomeFoiEditado.current,
      publicacao,
      canal,
      proximaTexto,
      detalhes,
      agora: agora?.toISOString() ?? null,
    });
  }, [aberta, onde, nome, publicacao, canal, proximaTexto, detalhes, agora]);

  // Para onde o foco volta depois de Desfazer. Quem o guarda é o fechamento
  // da folha: a `Folha` devolve o foco a quem a abriu na limpeza do próprio
  // efeito, que o React roda **antes** dos efeitos do pai — então, quando
  // esta linha corre, `activeElement` já é o botão "Registrar visita".
  // Sem isto, tocar em Desfazer tira da tela o botão que tem o foco e o foco
  // cai no `body`.
  const alvoDoFoco = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (aberta) return;
    const ativo = document.activeElement;
    alvoDoFoco.current =
      ativo instanceof HTMLElement && ativo !== document.body ? ativo : null;
  }, [aberta]);

  // Para onde o foco vai **ao abrir**. Sem isto a `Folha` deixaria o foco no
  // próprio diálogo, e antes ainda ele caía no botão "Fechar" — o primeiro
  // focável da ordem do documento, com os seis chips atrás dele. Depois do
  // toque 1, o que o VoiceOver tem de anunciar é o grupo dos resultados, não
  // a saída.
  const grupoDeChips = useRef<HTMLDivElement>(null);
  const grupoDaDuvida = useRef<HTMLDivElement>(null);

  // A pergunta nasce depois do toque num chip. Mover o foco para ela faz o
  // leitor anunciar a escolha nova e também a traz à vista quando o texto
  // grande a põe abaixo da dobra.
  useEffect(() => {
    if (duvida !== null) grupoDaDuvida.current?.focus();
  }, [duvida]);

  function alternarDetalhes(): void {
    if (detalhes) {
      // Fechar é só esconder: o que foi digitado continua valendo e continua
      // sendo gravado. Apagar aqui seria perder trabalho por um toque num
      // botão que só diz "mostre menos".
      setDetalhes(false);
      return;
    }
    setDetalhes(true);
    // **O toque que revela não pode levar embora o botão de salvar.**
    //
    // Em texto de acessibilidade os seis chips medem mais do que a folha
    // inteira (738px de chip num corpo de 517px, medido a 375×667 com a raiz
    // em 53px e a área segura do aparelho inteira — 59px em cima, 34px
    // embaixo), então o botão "Detalhes", que vem depois deles, só é
    // alcançável rolando: o corpo já está em 502 quando o dedo o encosta.
    // Abrir a revelação nesse ponto deixava os chips em -386–352 — dois dos
    // seis inteiros à vista, e os outros fora da tela por cima, sem aviso
    // nenhum de que o salvar tinha subido junto. Medido, não deduzido: a
    // rodada anterior supôs que "a folha já está em altura cheia, então
    // abrir não move nada" e a suposição estava errada — quem move não é a
    // revelação, é a rolagem que o próprio toque exigiu.
    //
    // `block: 'end'` e não `'start'`: alinhar o **fim** do grupo com o pé do
    // corpo é o menor movimento que devolve os chips (502 → 221, contra 0), e
    // deixa o que veio embaixo — a linha do "Onde?" e o começo da revelação —
    // logo abaixo da dobra, a um roçar de distância. Nos tamanhos normais
    // nada disso acontece: o grupo inteiro já cabe, a conta satura em zero e
    // a rolagem não se mexe.
    //
    // **O que esta rolagem custa, e é escolha:** o foco fica no "Detalhes",
    // como manda o padrão de disclosure do APG, e em AX5 o botão vem parar
    // inteiro abaixo da dobra depois do movimento. Para o VoiceOver não muda
    // nada (o cursor não anda e o `aria-expanded` é anunciado ali mesmo), mas
    // quem navega por teclado ou por Acesso Total ao Teclado perde o anel de
    // foco de vista. Preferimos isso a mover o foco: o chip **é** o botão de
    // salvar, e devolvê-lo à tela é o que o toque tinha de fazer. Registrado
    // em `docs/pendencias.md` — o destino é o mesmo dos encaixes de altura.
    grupoDeChips.current?.scrollIntoView({ block: 'end' });
    // A base da interpretação é fixada **agora**, no toque, e não a cada
    // renderização: é o que amarra a pré-visualização ao que se grava.
    setAgora(new Date());
    // As sugestões e o padrão da publicação numa leitura só:
    // `publicacoesUsadas` já vem da mais recente para a mais antiga, e a
    // primeira delas **é** `ultimaPublicacaoUsada`.
    void publicacoesUsadas(banco).then(
      (lista) => {
        setPublicacoes(lista);
        setPublicacao((atual) => atual ?? lista[0] ?? '');
      },
      () => {
        // Sem sugestões, o campo continua digitável — e o caminho curto
        // continua lendo a última publicação por conta própria na hora de
        // gravar. Uma leitura que falhou não pode impedir o registro.
        setPublicacoes([]);
      },
    );
  }

  /** Cria ou atualiza a ficha da pessoa e devolve o `id` para a visita
   *  apontar **junto com a foto de como a ficha estava antes** — é essa foto
   *  que o Desfazer põe de volta. `null` quando não há ficha nenhuma a fazer.
   *
   *  A ficha nasce com `revisita` ou `estudo` (spec §8): é o resultado que
   *  diz que há uma pessoa a revisitar. Nos outros quatro resultados a ficha
   *  **não nasce** — "não estava" não vira interessado —, mas se já existir
   *  alguém com aquele nome a visita se amarra a ela do mesmo jeito: é a
   *  mesma pessoa, e uma visita solta seria histórico perdido. */
  async function conciliarPessoa(
    intencao: IntencaoDeRegistro,
    existente: Pessoa | null,
    donoAtual: string,
  ): Promise<FichaMexida | null> {
    const viraFicha =
      intencao.resultado === 'revisita' || intencao.resultado === 'estudo';
    if (existente === null && (intencao.nome === '' || !viraFicha)) return null;
    const base = existente;
    const status: Pessoa['status'] = viraFicha
      ? intencao.resultado === 'estudo'
        ? 'estudo'
        : 'revisita'
      : (base?.status ?? 'interessado');
    const linha = await gravar(banco, 'pessoa', {
      id: base?.id ?? crypto.randomUUID(),
      user_id: base?.user_id ?? donoAtual,
      // O nome que a pessoa acabou de digitar manda: a busca casou por nome
      // normalizado (sem acento, sem caixa), então "jose" e "José" são a
      // mesma ficha — e a grafia mais recente é a que ela quis ver. Numa
      // ficha ligada, porém, o vazio transitório anterior ao preenchimento
      // não é uma edição e preserva o nome relido do banco.
      nome: base !== null && !intencao.nomeEditado ? base.nome : intencao.nome,
      apelido: base?.apelido ?? '',
      telefone: base?.telefone ?? '',
      // O endereço da ficha não vem do "Onde?" da visita: aquele campo é a
      // anotação da visita e pode conter qualquer coisa ("portão azul",
      // "voltar de manhã"). Endereço de pessoa se preenche na ficha dela.
      endereco: base?.endereco ?? '',
      lat: base?.lat ?? null,
      lng: base?.lng ?? null,
      idioma: base?.idioma ?? '',
      // Resultado que não faz ficha também não muda o status de quem já
      // existe: uma visita em que a pessoa não estava não a rebaixa.
      status,
      tags: base?.tags ?? [],
      nota: base?.nota ?? '',
      casa_id: base?.casa_id ?? null,
      // Sem data entendida, o que já estava agendado fica — **salvo quando
      // ele era o compromisso de hoje ou de antes**. A visita acabou de
      // acontecer: manter a data deixaria a pessoa na tira "Para visitar" de
      // Hoje todo santo dia, sem que ninguém tivesse pedido isso, e a tira
      // ficaria sem vazão nenhuma pelo caminho de dois toques. Vale para os
      // seis resultados, e não só para revisita/estudo: "não estava" também
      // é ter ido lá. O que fica combinado a partir de agora é uma decisão
      // nova, e ela se toma no campo "Próxima visita".
      proxima_visita_em:
        intencao.proxima?.em ??
        (ehCompromissoDeHoje(base?.proxima_visita_em)
          ? null
          : (base?.proxima_visita_em ?? null)),
    });
    return { id: linha.id, anterior: base };
  }

  /** Grava uma intenção já resolvida contra uma ficha específica. `null` é
   *  a escolha explícita por "Nova pessoa" (ou a ausência de candidata). */
  async function gravarCom(
    intencao: IntencaoDeRegistro,
    pessoaEscolhidaId: string | null,
    donoAtual: string,
    ultimaPublicacao: string | undefined,
  ): Promise<void> {
    // A candidata serve só para a escolha visual. No instante de persistir,
    // a ficha é relida pelo id dentro da mesma transação que pessoa, visita e
    // suas duas entradas da fila: uma mudança recente não é sobrescrita e
    // uma lápide não é ressuscitada. Os `gravar` abaixo participam desta
    // transação externa; seus sinais saem por `setTimeout`, depois do commit.
    const { ficha, linha } = await banco.transaction(
      'rw',
      banco.pessoa,
      banco.visita,
      banco.fila,
      async () => {
        const encontrada =
          pessoaEscolhidaId === null
            ? null
            : await banco.pessoa.get(pessoaEscolhidaId);
        if (
          pessoaEscolhidaId !== null &&
          (encontrada === null ||
            encontrada === undefined ||
            encontrada.deleted)
        ) {
          throw erroDeFichaObsoleta();
        }
        if (encontrada != null && encontrada.user_id !== donoAtual)
          throw new Error('Esta ficha pertence a outra conta.');
        const ficha = await conciliarPessoa(
          intencao,
          encontrada ?? null,
          donoAtual,
        );
        const linha = await gravar(banco, 'visita', {
          id: crypto.randomUUID(),
          user_id: donoAtual,
          pessoa_id: ficha?.id ?? null,
          casa_id: null,
          em: new Date().toISOString(),
          resultado: intencao.resultado,
          canal: intencao.canal,
          // **Um campo só para o endereço e para a nota**, e de propósito. A
          // `visita` não tem coluna de endereço (endereço é `casa_id`, e
          // `casa` exige `territorio_id`, que só nasce com o território),
          // então o "Onde?" já ia para `nota` desde o caminho curto.
          // Acrescentar um campo de nota separado poria dois controles
          // escrevendo na mesma coluna: o segundo apagaria o primeiro em
          // silêncio, e quem digitasse o endereço com a folha fechada o
          // perderia ao abrir os detalhes. Por isso o campo é um só — o
          // mesmo "Onde?", agora um `<textarea>` de verdade, que aceita o
          // ditado do teclado do iOS e cresce quando os detalhes abrem.
          // Quando o território existir, o endereço vira `casa_id` e este
          // campo volta a ser só nota.
          nota: intencao.onde,
          publicacao: intencao.publicacao ?? ultimaPublicacao ?? '',
          // O que ficou combinado, na letra da pré-visualização — e a letra
          // **diz a data**: "em 2 semanas (sábado, 21 de março, às 12h)". A
          // frase relativa sozinha era verdade no instante em que se digitou
          // e mentira uma semana depois, justamente na visita sem ficha de
          // pessoa, que é onde ela é o único registro do combinado. A
          // `pessoa` guarda o instante (`proxima_visita_em`); a visita guarda
          // a frase inteira, e é ela que sobra quando não há pessoa nenhuma.
          proxima_acao: intencao.proxima?.descricao ?? '',
        });
        return { ficha, linha };
      },
    );
    setRegistrada(linha);
    // A ficha vai junto: Desfazer desfaz o par, não só a visita.
    setFichaMexida(ficha);
    // O rascunho cumpriu o papel dele: a visita está no banco.
    limparRascunho();
    setDuvida(null);
    // Fechar só depois de a linha existir: falhou, a folha fica aberta com
    // o que a pessoa digitou e o recado do que houve.
    fecharFolha();
  }

  function fecharFolha(): void {
    versaoDaOperacao.current += 1;
    gravacaoEmCurso.current = false;
    setDuvida(null);
    setSalvando(null);
    setFichaEmGravacao(null);
    aoFechar();
  }

  function registrar(resultado: Resultado): void {
    if (gravacaoEmCurso.current || duvida !== null) return;
    gravacaoEmCurso.current = true;
    const operacao = ++versaoDaOperacao.current;
    setSalvando(resultado);
    setErroDeGravacao(null);
    // A mesma conta que a pré-visualização desenhou: mesma função pura,
    // mesmo texto, mesmo `agora`. Todo o resto também entra neste retrato: a
    // pergunta posterior decide só a identidade, não reabre a intenção.
    const intencao: IntencaoDeRegistro = {
      resultado,
      nome: nome.trim(),
      nomeEditado: nomeFoiEditado.current,
      onde: onde.trim(),
      publicacao,
      canal,
      proxima: lerProximaVisita(proximaTexto, agora),
    };
    void (async () => {
      // Os awaits que não são do Dexie ficam **fora** da transação: o dono
      // vem da sessão de autenticação e a publicação é uma leitura à parte.
      // Esperar por qualquer um deles dentro da transação a mataria
      // ("Transaction committed too early"). Cada `gravar` abre a sua.
      const [donoAtual, ultimaPublicacao, candidatas] = await Promise.all([
        dono(),
        // A leitura barata, que para na primeira visita com publicação — e
        // não a varredura de `publicacoesUsadas`, que é da abertura dos
        // detalhes. Esta corre dentro dos cinco segundos do caminho curto.
        ultimaPublicacaoUsada(banco),
        // A ligação por id tem precedência absoluta. Relemos a ficha para o
        // toque funcionar mesmo antes do efeito de abertura terminar, e para
        // jamais escolher outra homônima pelo nome.
        pessoaId === undefined
          ? pessoasPorNome(banco, intencao.nome)
          : banco.pessoa
              .get(pessoaId)
              .then((pessoa) =>
                pessoa === undefined || pessoa.deleted ? [] : [pessoa],
              ),
      ]);
      if (operacao !== versaoDaOperacao.current) return;
      // Duas fichas do mesmo nome não têm uma ordem que signifique
      // identidade. A escolha precisa vir de quem conhece as pessoas.
      if (pessoaId === undefined && candidatas.length > 1) {
        setDuvida({
          intencao,
          candidatas,
          donoAtual,
          ultimaPublicacao,
        });
        return;
      }
      await gravarCom(
        intencao,
        // O id explícito nunca vira ausência de candidata: se foi apagado,
        // a transação recusa e mantém a intenção, sem criar outra ficha nem
        // transformar uma visita vinculada em anônima.
        pessoaId ?? candidatas[0]?.id ?? null,
        donoAtual,
        ultimaPublicacao,
      );
    })()
      .catch((causa: unknown) => {
        if (operacao !== versaoDaOperacao.current) return;
        setErroDeGravacao(comoErro(causa));
      })
      .finally(() => {
        if (operacao !== versaoDaOperacao.current) return;
        gravacaoEmCurso.current = false;
        setSalvando(null);
      });
  }

  function escolherFicha(pessoaEscolhidaId: string | null): void {
    const pendente = duvida;
    if (pendente === null || gravacaoEmCurso.current) return;
    gravacaoEmCurso.current = true;
    const operacao = ++versaoDaOperacao.current;
    const chave = pessoaEscolhidaId ?? 'nova';
    setFichaEmGravacao(chave);
    setErroDeGravacao(null);
    void (async () => {
      // A pergunta pode ficar aberta indefinidamente. O dono capturado
      // serve para conferir a intenção, nunca para gravar após trocar conta.
      const donoAtual = await dono();
      if (operacao !== versaoDaOperacao.current) return;
      if (donoAtual !== pendente.donoAtual) {
        setDuvida(null);
        throw new Error('A conta mudou. Abra a folha novamente.');
      }
      await gravarCom(
        pendente.intencao,
        pessoaEscolhidaId,
        donoAtual,
        pendente.ultimaPublicacao,
      );
    })()
      .catch((causa: unknown) => {
        if (operacao !== versaoDaOperacao.current) return;
        setErroDeGravacao(comoErro(causa));
      })
      .finally(() => {
        if (operacao !== versaoDaOperacao.current) return;
        gravacaoEmCurso.current = false;
        setFichaEmGravacao(null);
      });
  }

  // O aviso vive fora da folha e o botão dele não tem como ficar
  // desabilitado daqui: a trava do toque repetido é esta, e é uma `ref`
  // porque nada na tela depende do valor.
  const desfazendo = useRef(false);
  function desfazer(): void {
    const linha = registrada;
    if (linha === null || desfazendo.current) return;
    desfazendo.current = true;
    const ficha = fichaMexida;
    void (async () => {
      // Lápide, nunca remoção: a linha pode já ter subido, e um `delete`
      // local a traria de volta no próximo pull.
      await apagar(banco, 'visita', linha.id);
      // **E a pessoa também.** Sem isto, desfazer uma "Revisita" com nome
      // deixava de pé exatamente o que o toque tinha criado: um interessado
      // órfão na tira "Para visitar" de Hoje, para sempre — ou uma ficha que
      // já existia com o status, a agenda e a grafia do nome trocados e sem
      // volta. A ordem é a inversa da gravação (lá a ficha vem antes da
      // visita) justamente para que nenhum instante tenha visita viva
      // apontando para ficha que já não está lá.
      if (ficha !== null) {
        if (ficha.anterior === null) {
          // Não existia antes: some. Lápide de novo, pela mesma razão.
          await apagar(banco, 'pessoa', ficha.id);
        } else {
          // Existia: volta a ser exatamente o que era. Os carimbos ficam de
          // fora — `gravar` põe os seus, e um `updated_at` velho seria
          // recusado pelo LWW e a ficha antiga nunca voltaria.
          const { updated_at, server_updated_at, ...comoEra } = ficha.anterior;
          void updated_at;
          void server_updated_at;
          await gravar(banco, 'pessoa', comoEra);
        }
      }
    })()
      .then(
        () => {
          setRegistrada(null);
          setFichaMexida(null);
          // Deu certo na segunda tentativa: o recado da primeira sai junto.
          setFalha(null);
          // O foco volta a quem abriu a folha **só aqui**, no caminho em que
          // o aviso de fato sumiu: o botão que o tinha acabou de sair da
          // tela e, sem isto, o foco cairia no `body`. Se a tentativa
          // falhasse, o "Desfazer" continuaria na tela para a segunda vez —
          // e mover o foco arrancaria dela justamente quem precisa tentar de
          // novo.
          const alvo = alvoDoFoco.current;
          if (alvo !== null && alvo.isConnected) alvo.focus();
        },
        (causa: unknown) => {
          // O aviso fica **com o "Desfazer" ainda ao lado**. Uma falha do
          // Dexie costuma ser passageira, e quem já disse que não queria
          // aquela visita não pode ficar sem segunda tentativa. O par não é
          // atômico: se a falha for na restauração da ficha, a visita já foi
          // sepultada e só a pessoa ficou mexida — por isso o texto não
          // promete em que estado as coisas pararam, e a segunda tentativa
          // refaz o par inteiro (as duas metades são idempotentes). O recado
          // não some sozinho: quem tem baixa visão lê devagar.
          setFalha(`Não deu para desfazer: ${comoErro(causa).message}`);
        },
      )
      .finally(() => {
        desfazendo.current = false;
      });
  }

  const mensagem = falha ?? (registrada === null ? null : 'Visita registrada');
  // Desfazer enquanto houver linha a desfazer — **inclusive depois de uma
  // falha**. Uma falha passageira do Dexie não pode ser tiro único: sem isto,
  // a visita ficava gravada e o aviso oferecia só "Dispensar", que é
  // exatamente o caminho sem volta.
  const podeDesfazer = registrada !== null;

  return (
    <>
      <Folha
        aberta={aberta}
        titulo="Registrar visita"
        focoInicial={grupoDeChips}
        aoFechar={fecharFolha}
      >
        <div className="registrar">
          {/* O nome do grupo é `aria-label`, e não um parágrafo visível.
              Em AX5 uma pergunta visível custaria uma linha de ~66px — um
              terço da altura de um chip —, e o que ela diria já está dito: a
              folha se chama "Registrar visita" e os seis botões dizem o que
              aconteceu. Quem ouve continua ouvindo "O que aconteceu?" ao
              entrar no grupo, que agora é onde o foco pousa ao abrir. */}
          <div
            ref={grupoDeChips}
            className="registrar__chips"
            role="group"
            aria-label="O que aconteceu?"
            // Focável por programa, nunca por navegação: é a primeira parada
            // ao abrir a folha, e daí em diante o Tab segue para os chips.
            tabIndex={-1}
          >
            {RESULTADOS.map((resultado) => (
              <Botao
                key={resultado}
                className="registrar__chip"
                variante="secundario"
                // O `carregando` do `Botao` marca só o chip tocado: é ele
                // quem está gravando, e é dele o `aria-busy` que o leitor de
                // tela anuncia. Desde a correção do design system o
                // `carregando` **não** desabilita de verdade — botão
                // desabilitado perde o foco e sai do rotor do VoiceOver, o
                // que aqui aconteceria justamente com o chip que a pessoa
                // acabou de tocar.
                carregando={salvando === resultado}
                // Os outros cinco não estão ocupados, estão indisponíveis
                // enquanto o registro corre: o que está em curso é a
                // gravação, não um chip. `aria-disabled` também neles, e
                // nunca `disabled` — quem percorre o grupo não pode ser
                // largado no meio. A trava de verdade continua sendo o
                // retorno antecipado de `registrar`.
                aria-disabled={
                  salvando !== null ||
                  fichaEmGravacao !== null ||
                  duvida !== null
                    ? true
                    : undefined
                }
                // Revisita e Estudo são os dois resultados que criam ficha, e
                // a prancha 02.1 os tinge por isso: âmbar e verde separam "a
                // visita acabou aqui" de "esta pessoa continua". O `data-` e
                // não uma classe por resultado — a cor é do significado, e o
                // CSS lê o significado.
                data-tinta={TINTA_DO_RESULTADO[resultado]}
                onClick={() => {
                  registrar(resultado);
                }}
              >
                {ICONE_DO_RESULTADO[resultado]}
                <span className="registrar__chip-rotulo">
                  {ROTULO_DO_RESULTADO[resultado]}
                </span>
              </Botao>
            ))}
          </div>

          {duvida === null ? null : (
            <div
              ref={grupoDaDuvida}
              className="registrar__duvida"
              role="group"
              aria-label={`Qual ${duvida.intencao.nome}?`}
              aria-describedby={idDicaDaDuvida}
              tabIndex={-1}
            >
              <p id={idDicaDaDuvida} className="registrar__duvida-dica">
                Há mais de uma ficha com esse nome. Escolha qual recebeu a
                visita.
              </p>
              {duvida.candidatas.map((pessoa) => (
                <Botao
                  key={pessoa.id}
                  variante="secundario"
                  largura="cheia"
                  carregando={fichaEmGravacao === pessoa.id}
                  aria-disabled={fichaEmGravacao !== null ? true : undefined}
                  onClick={() => {
                    escolherFicha(pessoa.id);
                  }}
                >
                  {pessoa.nome} · {pessoa.endereco || 'Sem endereço'} ·{' '}
                  {haQuantoTempo(pessoa.updated_at)}
                </Botao>
              ))}
              <Botao
                variante="texto"
                largura="cheia"
                carregando={fichaEmGravacao === 'nova'}
                aria-disabled={fichaEmGravacao !== null ? true : undefined}
                onClick={() => {
                  escolherFicha(null);
                }}
              >
                Nova pessoa
              </Botao>
            </div>
          )}

          {/* O campo e o botão dos detalhes dividem uma linha, e a razão é
              medida: a folha se ancora no pé da tela, então **tudo o que
              nasce abaixo dos chips os empurra para cima**. Medido no WebKit
              a 375×667 com a raiz em 17px (06/09/2026), o chip mais alto
              começa em 374 e a metade da tela está em 333: são 40px de folga
              contra os 51px de alvo do botão mais os 20px de respiro. Numa
              linha própria, o botão levaria a primeira fileira de chips para
              fora da zona do polegar — o "uma mão" da spec §8, que o e2e
              afirma por igualdade. Ao lado do campo, ele custa **zero**: a
              linha já tinha a altura do campo. Em texto de acessibilidade a
              linha quebra e o botão desce sozinho, e ali isso não custa chip
              nenhum — a folha já está em altura cheia e os chips ficam no
              topo do corpo que rola. */}
          <div className="registrar__linha">
            {/* Rótulo curto e pista no `placeholder`, não numa linha de ajuda:
                medido a 17px (06/09/2026), a linha de ajuda empurrava os seis
                chips 35px para cima e a primeira fileira saía da metade
                inferior da tela — o "uma mão" da spec §8. O rótulo continua
                visível e permanente; a pista é que é dispensável. A dica só
                aparece com os detalhes abertos, onde não custa chip. */}
            {/* O rótulo é "Onde?", e não "Nota", porque enquanto não houver
                território é o papel de endereço que domina: o campo é o único
                lugar em que a casa visitada fica registrada, e é isso que a
                pessoa procura ao abrir a folha na porta. A anotação livre
                cabe no mesmo campo — a dica diz isso quando os detalhes
                abrem —, mas o nome do controle acompanha o uso principal.
                Quando `territorio`/`casa` existirem, o endereço vira
                `casa_id` e o rótulo deixa de ser "Onde?". */}
            <CampoLongo
              rotulo="Onde?"
              placeholder="Endereço ou ponto de referência"
              {...(detalhes
                ? {
                    dica: 'Endereço, ponto de referência ou o que você quiser anotar sobre a visita.',
                  }
                : {})}
              // Uma linha fechado — a mesma altura do campo curto que estava
              // aqui —, três com os detalhes abertos, que é quando ele vira
              // campo de anotação.
              rows={detalhes ? 3 : 1}
              value={onde}
              onChange={(evento) => {
                setOnde(evento.target.value);
              }}
              autoComplete="off"
            />
            {/* "Detalhes", e não "Mais detalhes": o nome curto devolve ~40px
                de largura ao campo "Onde?", que divide a linha com ele, e não
                custa um pixel de altura. O nome é o mesmo nos dois estados,
                como manda o padrão de disclosure do APG — quem diz se está
                aberto é o `aria-expanded`, e dois nomes para o mesmo controle
                atrapalhariam quem navega por Controle por Voz. */}
            <Botao
              className="registrar__mais"
              variante="secundario"
              aria-expanded={detalhes}
              aria-controls={idDetalhes}
              onClick={alternarDetalhes}
            >
              Detalhes
            </Botao>
          </div>

          {/* Escondido pelo atributo, e o `display: none` está na folha de
              estilo junto — sem ele, qualquer `display` de classe venceria o
              `hidden` (a armadilha do CLAUDE.md) e a região ficaria visível
              com `aria-expanded="false"`. A prova é do Playwright: no jsdom
              não há folha de estilo e `toBeVisible()` consulta só o
              atributo. */}
          <div
            id={idDetalhes}
            className="registrar__detalhes"
            hidden={!detalhes}
          >
            <Campo
              rotulo="Nome"
              placeholder="Como você a chama"
              // Dizer o que o campo faz é o que impede a surpresa: com
              // "Revisita" ou "Estudo" o nome vira ficha; com os outros
              // quatro resultados ele só amarra a visita a uma ficha que já
              // exista. Sem este aviso, quem digitasse um nome com "Não
              // estava" acharia que tinha cadastrado alguém.
              dica="Com Revisita ou Estudo, o nome vira ficha de pessoa."
              value={nome}
              onChange={(evento) => {
                nomeFoiEditado.current = true;
                setNome(evento.target.value);
              }}
              autoComplete="off"
              enterKeyHint="next"
            />
            <CampoPublicacao
              valor={publicacao ?? ''}
              sugestoes={publicacoes}
              aoMudar={setPublicacao}
            />
            {/* Aqui o `GrupoDeChips` é o certo, ao contrário dos seis de
                cima: escolher um canal é só escolher, e a seta do teclado
                percorrendo o grupo não grava nada. */}
            <GrupoDeChips
              nome="canal-da-visita"
              rotulo="Canal"
              opcoes={OPCOES_DE_CANAL}
              valor={canal}
              aoMudar={setCanal}
            />
            <CampoProximaVisita
              texto={proximaTexto}
              agora={agora}
              aoMudar={setProximaTexto}
            />
          </div>

          {erroDeGravacao ? (
            <p className="registrar__erro" role="alert">
              Não deu para registrar: {erroDeGravacao.message}
            </p>
          ) : null}
        </div>
      </Folha>

      {/* Fora da `Folha`, e de propósito: o aviso nasce no instante em que a
          folha fecha. O `Toast` mantém as duas regiões vivas montadas — a do
          `body` e a da folha aberta —, então a mensagem chega a uma região
          que já estava lá, que é a única que o leitor de tela anuncia. */}
      <Toast
        mensagem={mensagem}
        {...(podeDesfazer
          ? { acao: { rotulo: 'Desfazer', aoAcionar: desfazer } }
          : {})}
        {...(falha === null
          ? {}
          : {
              // Só o recado de falha ganha "Dispensar", e é ele que dá a
              // saída: o "Desfazer" ao lado continua sendo a segunda
              // tentativa, e sem "Dispensar" o aviso ficaria na tela para
              // sempre. O de sucesso some sozinho em seis segundos e não
              // precisa de um alvo a mais para errar.
              aoDispensar: () => {
                setFalha(null);
                setRegistrada(null);
                setFichaMexida(null);
              },
            })}
        // O recado de falha não some sozinho: `0` desliga a contagem, e ele
        // fica até alguém tentar de novo ou dispensar. Quem tem baixa visão
        // lê devagar, e agora há uma ação a tomar embaixo dele.
        segundos={falha === null ? SEGUNDOS_DE_DESFAZER : 0}
      />
    </>
  );
}

/** `FolhaRegistrarCom` amarrada ao banco e à sessão do app. É o que a rota
 *  monta. */
export function FolhaRegistrar({
  aberta,
  pessoaId,
  aoFechar,
}: Pick<DepsDeRegistrar, 'aberta' | 'pessoaId' | 'aoFechar'>) {
  return (
    <FolhaRegistrarCom
      banco={bancoDoApp}
      dono={donoDoApp}
      aberta={aberta}
      {...(pessoaId === undefined ? {} : { pessoaId })}
      aoFechar={aoFechar}
    />
  );
}
